import React, { useEffect } from 'react';
import { navigate } from 'gatsby';
import { useTranslation } from 'react-i18next';
import { Button, Stack } from '@mui/material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { Controller, useForm, useWatch } from 'react-hook-form';
import dayjs, { Dayjs } from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { DEFAULT_WHEELS_TIME_FILTERS, WheelsTimeFilters } from '../../../domain/wheels';
import { storeWheelsTimeFiltersInLocalStorage } from '../utils';

export const WheelsTimeFiltersForm = () => {
  const { t } = useTranslation();

  const { control, formState, handleSubmit, clearErrors } = useForm({ defaultValues: DEFAULT_WHEELS_TIME_FILTERS });

  // == Extensions ================================================================
  dayjs.extend(isSameOrAfter);

  // == Effects ================================================================
  //if the exitDate field has a date before today, shows the error
  const exitDate = useWatch({ control, name: 'exitDate' });
   useEffect(() => {
      if (dayjs(exitDate).isBefore(dayjs(), 'day')) {
        control.setError('exitDate', { message: t('wheels_service.inputs.exit_date_error') });
      }else clearErrors('exitDate');
    
  }, [exitDate]);

  // == Handlers ================================================================
  const validateExitTime = (value: string) => {
    const time = dayjs(value, 'HH:mm');
    const minTime = dayjs().hour(9).minute(0);  // 09:00 AM
    const maxTime = dayjs().hour(23).minute(0); // 11:00 PM

    if (time.isBefore(minTime) || time.isAfter(maxTime)) {
      control.setError('exitTime', { message: t('wheels_service.inputs.exit_time_error') });
      return false;
    }else clearErrors('exitTime');
    return true;
  }

  const submitWheelsForm = (timeFilters: WheelsTimeFilters) => {
    storeWheelsTimeFiltersInLocalStorage(timeFilters);

    navigate('/checkout/wheels');
  };

  const handleExitDateChange = (onChange: (date: string) => void) => (date: Dayjs | null) => {
    if (!date) return;
    if (!date.isValid()) return;

    onChange(date.format('YYYY-MM-DD'));
  };

  const handleExitTimeChange = (onChange: (time: string) => void) => (time: Dayjs | null) => {
    if (!time) return;
    if (!time.isValid()) return;

    validateExitTime(time.format('HH:mm'));
    onChange(time.format('HH:mm'));
  };

  return (
    <>
      <Stack gap={3}>
        <Controller
          control={control}
          name="exitDate"
          rules={{ required: true, validate: (value) => dayjs(value).isSameOrAfter(dayjs(), 'day') }} 
          render={({ field }) => (
            <DatePicker
              value={field.value ? dayjs(field.value) : null}
              label={t('wheels_service.inputs.exit_date')}
              minDate={dayjs()}
              onChange={handleExitDateChange(field.onChange)}
              disablePast
              slotProps={{
                textField: {
                  variant: 'outlined',
                  error: !!formState.errors.exitDate,
                  onBlur: field.onBlur,
                  helperText: formState.errors.exitDate
                    ? t(`${formState.errors.exitDate.message}`)
                    : '',
                },
              }}
            />
          )}
        />

        <Controller
          control={control}
          name="exitTime"
          rules={{ required: true, validate: validateExitTime }}
          render={({ field, fieldState }) => (
            <TimePicker
              ampm={false}
              minTime={dayjs(new Date(0, 0, 0, 9))}
              maxTime={dayjs(new Date(0, 0, 0, 23, 30))}
              timeSteps={{ minutes: 30 }}
              value={field.value ? dayjs(field.value, 'HH:mm') : null}
              label={t('wheels_service.inputs.exit_time')}
              onChange={handleExitTimeChange(field.onChange)}
              slotProps={{
                textField: {
                  variant: 'outlined',
                  error: !!fieldState?.error,
                  onBlur: field.onBlur,
                  helperText: fieldState?.error
                    ? t(`${fieldState?.error.message}`)
                    : '',
                },
              }}
            />
          )}
        />
      </Stack>

      <Stack alignItems="flex-end">
        <Button
          disabled={!formState.isValid}
          size="large"
          variant="contained"
          sx={{ width: 200 }}
          onClick={handleSubmit(submitWheelsForm)}
        >
          {t('wheels_service.form_button')}
        </Button>
      </Stack>
    </>
  );
};
